import type { FC } from 'react';
import React, { Fragment, useEffect, useRef, useCallback } from 'react';
import { styled } from '@compiled/react';
import { injectIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';

import GenericError from '../assets/GenericError.svg';

import { ContentStateForm } from './ContentStateForm.tsx';
import { ContentStateGroup } from './ContentStateGroup.tsx';
import { RemoveButton } from './RemoveButton.tsx';
import type { ContentStateMenuProps } from './types.ts';
import { customGroupLabel, suggestedGroupLabel } from './types.ts';
import { i18n } from './i18n.ts';

type ContentStateMenuWrapperProps = {
	ref?: any;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentStateMenuWrapper = styled.div<ContentStateMenuWrapperProps>({
	padding: `${token('space.150')} 0`,
	width: '284px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	margin: `${token('space.0')} ${token('space.200')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FormWrapper = styled.div({
	margin: `${token('space.0')} ${token('space.200')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HorizontalDivider = styled.div({
	backgroundColor: token('color.background.neutral'),
	height: '1px',
	width: '260px',
	margin: `${token('space.150')} auto ${token('space.250')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentStateGroupsWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'& > div:not(:last-child)': {
		marginBottom: token('space.250'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentStateDisabledDiv = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeatureDisabledGraphic = styled.img({
	height: '70px',
	width: '50px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeatureDisabledTextWrapper = styled.p({
	color: token('color.text.disabled'),
	margin: token('space.150'),
	textAlign: 'center',
});

const ContentStateMenuComponent: FC<ContentStateMenuProps> = ({
	contentId,
	contentStateState,
	contentType,
	isContentStateEnabled,
	isCustomEnabled,
	defaultStatesArray,
	customStatesArray,
	isSuggestedEnabled,
	isCreatingContentState,
	selectedColor,
	onSelectColor,
	isColorPickerOpen,
	onColorPickerClose,
	onColorPickerToggle,
	onSubmit,
	onRemove,
	onCancel,
	onFocusTextField,
	onMouseEnterRemove,
	onMouseLeaveRemove,
	userOffline,
	onPopupClose,
	contentStateButtonRef,
	intl: { formatMessage },
	onUpdateSuccess,
	isContentStateMenuOpen,
	setIsContentStateMenuOpen,
	updatePopup,
}) => {
	const menuComponent = useRef<HTMLElement | null>(null);
	const colorPickerComponent = useRef<HTMLElement | null>(null);

	// If user clicks outside the hover menu, color picker component, or content state button, close the menu.
	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (
				menuComponent?.current &&
				event.target instanceof Node &&
				!menuComponent?.current?.contains(event.target) &&
				!colorPickerComponent?.current?.contains(event.target) &&
				!contentStateButtonRef?.current?.contains(event.target)
			) {
				onPopupClose();
			}
		},
		[onPopupClose, contentStateButtonRef],
	);

	const handleKeydown = useCallback(
		(event: KeyboardEvent) => {
			// If user hits esc key and the color picked is open, assume they want to close that.
			// Otherwise, close the whole modal.
			if (event.key === 'Escape' && isContentStateMenuOpen) {
				if (isColorPickerOpen) {
					onColorPickerClose();
				} else {
					onPopupClose();
				}
			}
		},
		[isContentStateMenuOpen, isColorPickerOpen, onColorPickerClose, onPopupClose],
	);

	useEffect(() => {
		// Something is wrong where atlaskit popup component isn't automatically closing when user hits esc key (I think due to
		// because of nested popups). Therefore, we need to check if user hits escape key to close the popups
		document.addEventListener('keydown', handleKeydown);
		return () => {
			document.removeEventListener('keydown', handleKeydown);
		};
		// need to add isColorPickerOpen to dep array since event doesn't get updated state - https://stackoverflow.com/a/53846698
	}, [isColorPickerOpen, handleKeydown]);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [handleClickOutside]);

	const didRender = useRef(false);
	useEffect(() => {
		if (!didRender.current) {
			didRender.current = true;
			return;
		}

		// Reposition popup if content states load in after the popup is opened.
		// Prevents the popup from extending beyond the viewport.
		updatePopup();
	}, [defaultStatesArray, customStatesArray, updatePopup]);

	return (
		<ContentStateMenuWrapper ref={menuComponent as any}>
			<HeaderWrapper>
				<Heading size="xsmall" as="h4">
					{contentType === 'blogpost'
						? formatMessage(i18n.setBlogStatus)
						: formatMessage(i18n.setPageStatus)}
				</Heading>
				<RemoveButton
					hasContentState={!!contentStateState.state.contentState}
					onClick={onRemove}
					onMouseEnter={onMouseEnterRemove}
					onMouseLeave={onMouseLeaveRemove}
					userOffline={userOffline}
				/>
			</HeaderWrapper>
			{isCustomEnabled && isContentStateEnabled && (
				<FormWrapper data-testid="form-wrapper">
					<ContentStateForm
						onSubmit={onSubmit}
						onCancel={onCancel}
						showFormFooter={isCreatingContentState}
						selectedColor={selectedColor}
						onSelectColor={onSelectColor}
						isColorPickerOpen={isColorPickerOpen}
						onColorPickerToggle={onColorPickerToggle}
						onColorPickerClose={onColorPickerClose}
						onFocusTextField={onFocusTextField}
						userOffline={userOffline}
						colorPickerComponentRef={colorPickerComponent}
					/>
				</FormWrapper>
			)}
			{/* If user focuses on color picker or name input then other content states
      are hidden and submit and cancel buttons are shown in their place */}
			{isContentStateEnabled ? (
				!isCreatingContentState && (
					<Fragment>
						<HorizontalDivider />
						<ContentStateGroupsWrapper data-testid="content-state-group">
							<ContentStateGroup
								contentId={contentId}
								contentStateState={contentStateState}
								groupIsEnabled={isCustomEnabled}
								groupLabel={customGroupLabel}
								contentStates={customStatesArray}
								userOffline={userOffline}
								onUpdateSuccess={onUpdateSuccess}
								setIsContentStateMenuOpen={setIsContentStateMenuOpen}
							/>
							<ContentStateGroup
								contentId={contentId}
								contentStateState={contentStateState}
								groupIsEnabled={isSuggestedEnabled}
								groupLabel={suggestedGroupLabel}
								contentStates={defaultStatesArray}
								userOffline={userOffline}
								onUpdateSuccess={onUpdateSuccess}
								setIsContentStateMenuOpen={setIsContentStateMenuOpen}
							/>
						</ContentStateGroupsWrapper>
					</Fragment>
				)
			) : (
				<ContentStateDisabledDiv data-testid="content-state-disabled">
					<HorizontalDivider />
					{/* TODO: Update the attribute below so it is translated. */}
					{/* eslint-disable-next-line no-restricted-syntax */}
					<FeatureDisabledGraphic src={GenericError} alt="content states disabled graphic" />
					<FeatureDisabledTextWrapper data-testid="feature-disabled-text">
						{formatMessage(i18n.featureDisabled)}
					</FeatureDisabledTextWrapper>
				</ContentStateDisabledDiv>
			)}
		</ContentStateMenuWrapper>
	);
};

export const ContentStateMenu = injectIntl(ContentStateMenuComponent);
